import { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Loader2, AlertCircle } from 'lucide-react';
import { DataTable, TableColumn } from '@/components/shared/data-table';
import { MissedStandardsFilters } from '@/components/assessments/missed-standards-filters';
import {
  useMissedStandardsQuery,
  type MissedStandard,
} from '@/hooks/queries/useMissedStandards';
import { cn } from '@/lib/utils';

export default function MidStandardPage() {
  const { id: propertyId, assessmentId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRTL = useMemo(() => i18n.language === 'ar', [i18n.language]);

  const [currentPage, setCurrentPage] = useState(1);
  const [departmentFilter, setDepartmentFilter] = useState('all-departments');
  const [customDateInput, setCustomDateInput] = useState('');
  const itemsPerPage = 10;

  // Fetch missed standards from API
  const {
    data: missedStandardsResponse,
    isLoading,
    error,
  } = useMissedStandardsQuery(assessmentId, {
    page: currentPage,
    page_size: itemsPerPage,
    ...(customDateInput && { date: customDateInput }),
    // Note: department_id filter requires mapping department name to ID
    // For now, we'll do client-side filtering for departments
  });

  const allStandards = missedStandardsResponse?.data || [];

  // Client-side department filtering (until we have department ID mapping)
  const filteredStandards = allStandards.filter(standard => {
    if (departmentFilter === 'all-departments') return true;
    return standard.department === departmentFilter;
  });

  // Get unique departments for filter
  const departments = useMemo(() => {
    return [...new Set(allStandards.map(s => s.department))];
  }, [allStandards]);

  // Mock property name - TODO: fetch from assessment details
  const propertyName = 'Property';

  // Pagination from API
  const totalPages = missedStandardsResponse?.pagination?.last_page || 1;
  const totalCount = missedStandardsResponse?.pagination?.total || 0;

  const handlePageChange = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const handleBack = () => {
    navigate(`/properties/${propertyId}`);
  };

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [customDateInput, departmentFilter]);

  // Define table columns
  const columns: TableColumn<MissedStandard>[] = [
    {
      key: 'dateAnswered',
      label: t('missedStandards.table.columns.date'),
      render: item => new Date(item.dateAnswered).toLocaleDateString('en-US'),
    },
    {
      key: 'department',
      label: t('missedStandards.table.columns.department'),
      className: 'font-medium',
    },
    {
      key: 'touchpoint',
      label: t('missedStandards.table.columns.touchpoint'),
    },
    {
      key: 'title',
      label: t('missedStandards.table.columns.title'),
    },
    {
      key: 'text',
      label: t('missedStandards.table.columns.standard'),
      render: item => (
        <div className="max-w-md">
          <p className="text-sm" title={item.text}>
            {item.text.length > 50
              ? `${item.text.substring(0, 50)}...`
              : item.text}
          </p>
        </div>
      ),
    },
    {
      key: 'comment',
      label: t('missedStandards.table.columns.comment'),
      render: item => (
        <div className="max-w-md">
          <p className="text-sm text-muted-foreground">
            {item.comment || t('missedStandards.table.noComment')}
          </p>
        </div>
      ),
    },
  ];

  return (
    <div className={cn('min-h-screen bg-background', isRTL && 'rtl')}>
      {/* Header */}
      <div className="bg-primary text-primary-foreground p-4 sm:p-6">
        <div className="max-w-7xl mx-auto">
          <div
            className={cn(
              'flex items-center justify-between flex-wrap gap-4',
              isRTL ? 'flex-row-reverse' : 'flex-row'
            )}
          >
            <Button
              variant="ghost"
              onClick={handleBack}
              className={cn(
                'text-primary-foreground hover:bg-primary-foreground/10',
                isRTL ? 'flex-row-reverse' : 'flex-row'
              )}
            >
              <ArrowLeft className={cn('w-4 h-4', isRTL ? 'ml-2' : 'mr-2')} />
              {t('missedStandards.backButton')}
            </Button>

            <div className={cn('text-center flex-1')}>
              <h1 className="text-2xl font-semibold">
                {t('missedStandards.pageTitle')}
              </h1>
              <p className="text-primary-foreground/80">
                {t('missedStandards.pageSubtitle', { propertyName })}
              </p>
            </div>

            {/* Spacer for alignment */}
            <div className="w-[100px]"></div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto p-6">
        {/* Filters */}
        <MissedStandardsFilters
          customDateInput={customDateInput}
          departmentFilter={departmentFilter}
          departments={departments}
          onDateChange={setCustomDateInput}
          onDepartmentChange={setDepartmentFilter}
          onClearFilters={() => {
            setDepartmentFilter('all-departments');
            setCustomDateInput('');
            setCurrentPage(1);
          }}
          assessmentId={assessmentId!}
          filteredCount={totalCount}
        />

        {/* Loading State */}
        {isLoading && (
          <Card>
            <CardContent className="py-12">
              <div className="flex flex-col items-center gap-4">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <p className="text-sm text-muted-foreground">
                  {t('missedStandards.loading', 'Loading missed standards...')}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Error State */}
        {error && (
          <Card>
            <CardContent className="py-12">
              <div className="flex flex-col items-center gap-4 text-center">
                <AlertCircle className="h-8 w-8 text-destructive" />
                <div>
                  <p className="font-medium text-destructive">
                    {t(
                      'missedStandards.error',
                      'Failed to load missed standards'
                    )}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {error.message}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Standards Table using DataTable Component */}
        {!isLoading && !error && (
          <Card>
            <CardHeader>
              <div
                className={cn(
                  'flex items-center justify-between',
                  isRTL ? 'flex-row-reverse' : 'flex-row'
                )}
              >
                <div className={cn(isRTL && 'text-right')}>
                  <CardTitle>{t('missedStandards.table.title')}</CardTitle>
                  <CardDescription>
                    {t('missedStandards.table.description')}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <DataTable
                data={filteredStandards}
                columns={columns}
                pagination={{
                  current: currentPage,
                  total: totalPages,
                  count: totalCount,
                  itemsPerPage: itemsPerPage,
                }}
                onPageChange={handlePageChange}
                emptyMessage={t('missedStandards.table.noStandards')}
                tableCaption={t('missedStandards.table.title')}
              />
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
